import { useQuery } from '@apollo/client';

import { useMedplum } from './MedplumProvider';
import { PATIENT_BY_IDENTIFIER_QUERY } from './fhirQueries';

interface MedplumPatientAccessResult {
  isLoading: boolean;
  hasAccess: boolean;
  fhirPatientId: string | null;
}

/**
 * Checks whether the current user has Medplum access to the given Eluve
 * patient.
 *
 * Executes a FHIR GraphQL query for `PatientList(identifier: $id)`. The
 * tenant-member AccessPolicy filters
 * `Patient?general-practitioner=%profile`, so the query only returns the
 * Patient when the current provider is linked as GP. Tenant admins have no
 * AccessPolicy, so this returns true for any synced Patient.
 *
 * Returns `hasAccess: false` when the tenant does not have the MEDPLUM
 * feature flag enabled, no Medplum project exists, the Apollo FHIR client
 * has not been initialized yet, or the GraphQL response is empty.
 */
export const useMedplumPatientAccess = (
  eluvePatientId: string | undefined,
): MedplumPatientAccessResult => {
  const { enabled, fhirClient } = useMedplum();

  const { data, loading } = useQuery(PATIENT_BY_IDENTIFIER_QUERY, {
    client: fhirClient ?? undefined,
    variables: { identifier: eluvePatientId ?? '' },
    skip: !enabled || !fhirClient || !eluvePatientId,
    fetchPolicy: 'cache-and-network',
  });

  const patient = data?.PatientList?.[0] ?? null;

  return {
    isLoading: loading,
    hasAccess: Boolean(patient),
    fhirPatientId: patient?.id ?? null,
  };
};
